/**
 * Country picker.
 *
 * The parser's country guess is exactly that — a guess — so the histogram is
 * shown with real counts and the unmatched rows get their own explicit "Other"
 * bucket rather than being silently dropped or silently included.
 */

import { useEffect, useMemo, useState } from 'react';
import type { CountryBucket } from '@sflx/shared';
import { countryLabel, flagEmoji } from '@sflx/shared';
import { EmptyState, ErrorNotice, Loading, Spinner } from '../components/Feedback';
import { Icon } from '../components/Icon';
import { accounts as accountsApi } from '../lib/api';
import { useAccounts } from '../lib/accounts-store';
import { useSession } from '../lib/session';
import { formatCount } from '../lib/format';
import { navigate } from '../router/router';

export function CountryPicker({ accountId }: { accountId: string }) {
  const { accounts, reload } = useAccounts();
  const { user } = useSession();
  const account = accounts.find((candidate) => candidate.id === accountId) ?? null;

  const [buckets, setBuckets] = useState<CountryBucket[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [includeOther, setIncludeOther] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    if (user?.role !== 'admin') navigate('/accounts');
  }, [user?.role]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    accountsApi
      .countries(accountId)
      .then((result) => {
        if (!cancelled) setBuckets(result.countries);
      })
      .catch((caught: Error) => {
        if (!cancelled) setError(caught);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, reloads]);

  // Seed from the saved selection once the account row is available. `null`
  // means "no filter chosen yet", which is not the same as "none selected".
  useEffect(() => {
    if (hydrated || !account) return;
    setSelected(new Set(account.liveCountryCodes ?? []));
    setIncludeOther(account.includeOther);
    setHydrated(true);
  }, [account, hydrated]);

  const sorted = useMemo(
    () =>
      (buckets ?? [])
        .filter((bucket) => bucket.code !== null)
        .slice()
        .sort((left, right) => right.count - left.count),
    [buckets],
  );

  const other = (buckets ?? []).find((bucket) => bucket.code === null) ?? null;

  const toggle = (code: string) =>
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await accountsApi.update(accountId, {
        // Always an array, never null: an empty selection is a decision
        // ("just show me the ones nothing could be read from"), and sending
        // null would store it as "never chosen" — which means everything.
        liveCountryCodes: Array.from(selected),
        includeOther,
      });
      await reload();
      navigate('/live');
    } catch (caught) {
      setError(caught);
    } finally {
      setSaving(false);
    }
  };

  // Order matters: a failed load leaves `buckets` null forever, so checking it
  // first renders a spinner that never resolves and never says why.
  if (error && !buckets) {
    return (
      <div className="page">
        <div className="wrap stack">
          <ErrorNotice error={error} />
          <button type="button" className="btn" onClick={() => setReloads((value) => value + 1)}>
            Try again
          </button>
          <button type="button" className="btn btn--ghost" onClick={() => navigate('/accounts')}>
            Back to accounts
          </button>
        </div>
      </div>
    );
  }

  if (!buckets) {
    return (
      <div className="page">
        <Loading label="Reading the channel histogram" />
      </div>
    );
  }

  const totalSelected =
    sorted.filter((bucket) => bucket.code && selected.has(bucket.code)).reduce((sum, bucket) => sum + bucket.count, 0) +
    (includeOther && other ? other.count : 0);

  return (
    <div className="page">
      <div className="wrap stack">
        <div className="page__head">
          <div className="page__title">
            <h1>Live channels by country</h1>
            <span className="muted">
              {account ? `${account.label} · ` : ''}
              Pick the countries you actually watch. Everything else stays out of the channel list.
            </span>
          </div>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => navigate('/accounts')}>
            <Icon name="chevronLeft" size={15} />
            Accounts
          </button>
        </div>

        <ErrorNotice error={error} />

        {!sorted.length && !other ? (
          <EmptyState
            title="No channels to sort yet"
            hint="This appears once a live fetch has finished. Try Refresh on the account."
          />
        ) : (
          <>
            <div className="row" role="group" aria-label="Countries">
              {sorted.map((bucket) => {
                const code = bucket.code as string;
                const on = selected.has(code);
                return (
                  <button
                    key={code}
                    type="button"
                    className="chip"
                    aria-pressed={on}
                    onClick={() => toggle(code)}
                  >
                    <span aria-hidden="true">{flagEmoji(code)}</span>
                    {countryLabel(code)}
                    <span className="chip__count">{formatCount(bucket.count)}</span>
                  </button>
                );
              })}
            </div>

            {other ? (
              <div className="stack stack--tight">
                <span className="label">Unmatched</span>
                <div className="row">
                  <button
                    type="button"
                    className="chip"
                    aria-pressed={includeOther}
                    onClick={() => setIncludeOther((value) => !value)}
                  >
                    Other
                    <span className="chip__count">{formatCount(other.count)}</span>
                  </button>
                  <span className="muted" style={{ fontSize: 13 }}>
                    Channels whose category name carried no country the parser could read.
                  </span>
                </div>
              </div>
            ) : null}

            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="muted">
                {selected.size || includeOther
                  ? `${formatCount(totalSelected)} channels selected`
                  : 'Nothing selected — the whole catalog stays visible.'}
              </span>
              <div className="row">
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => {
                    setSelected(new Set());
                    setIncludeOther(false);
                  }}
                >
                  Clear
                </button>
                <button type="button" className="btn btn--primary" onClick={() => void save()} disabled={saving}>
                  {saving ? <Spinner /> : null}
                  Save selection
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
